import { RelativeStart } from "@/components/View/Agent/Assignment/types/RelativeStart";
import { Status } from "@/components/View/Agent/Assignment/types/Status";
import { isUuid, Uuid } from "@/types/Brands/Uuid";
import { enumHasValue } from "@/types/Enums";
import { HttpMethod } from "@/types/Enums/HttpMethod";
import { HttpStatusCode } from "@/types/Enums/HttpStatusCode";
import { ifInObject, inObject, isObject } from "@/types/TypeGuard";
import {
  AddOperation,
  applyPatch,
  Operation,
  ReplaceOperation,
} from "fast-json-patch";
import log from "loglevel";
import moment from "moment-timezone";
import { TimeZone } from "../TimeZone";
import { ApiRequest, ApiResponse } from "./types";

export class DemoStorage {
  private static DB_NAME = "OrebroDemoData";
  private static DB_VERSION = 6;
  private static STORES = {
    LMA_NUMBERS: "lmaNumbers",
    CUSTOMER_FILES: "customerFiles",
    ASSIGNMENT_PATCHES: "assignmentPatches",
    FEEDBACK_PROCESSING_COMMENTS: "feedbackProcessingComments",
    AGENCY_SETTINGS: "agencySettings",
    FEEDBACK_TYPE_WEIGHTS: "feedbackTypeWeights",
  };
  private static MAX_CUSTOMER_FILES = 50;

  private static async getDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object stores
        if (!db.objectStoreNames.contains(this.STORES.LMA_NUMBERS)) {
          db.createObjectStore(this.STORES.LMA_NUMBERS, {
            keyPath: "assignmentId",
          });
        }
        if (!db.objectStoreNames.contains(this.STORES.CUSTOMER_FILES)) {
          const customerFilesDb = db.createObjectStore(
            this.STORES.CUSTOMER_FILES,
            { autoIncrement: true }
          );
          customerFilesDb.createIndex("assignmentId", "assignmentId");
          customerFilesDb.createIndex("fileId", "fileId", { unique: true });
        }
        if (!db.objectStoreNames.contains(this.STORES.ASSIGNMENT_PATCHES)) {
          db.createObjectStore(this.STORES.ASSIGNMENT_PATCHES, {
            keyPath: "assignmentId",
          });
        }
        if (
          !db.objectStoreNames.contains(
            this.STORES.FEEDBACK_PROCESSING_COMMENTS
          )
        ) {
          db.createObjectStore(this.STORES.FEEDBACK_PROCESSING_COMMENTS, {
            keyPath: "feedbackId",
          });
        }
        if (!db.objectStoreNames.contains(this.STORES.AGENCY_SETTINGS)) {
          db.createObjectStore(this.STORES.AGENCY_SETTINGS, {
            keyPath: "agencyId",
          });
        }
        if (!db.objectStoreNames.contains(this.STORES.FEEDBACK_TYPE_WEIGHTS)) {
          db.createObjectStore(this.STORES.FEEDBACK_TYPE_WEIGHTS, {
            keyPath: "feedbackTypeId",
          });
        }
      };
    });
  }

  static async storeLmaNumber(
    assignmentId: Uuid,
    lmaNumber: string
  ): Promise<void> {
    const db = await this.getDB();
    const transaction = db.transaction([this.STORES.LMA_NUMBERS], "readwrite");
    const store = transaction.objectStore(this.STORES.LMA_NUMBERS);

    return new Promise((resolve, reject) => {
      const request = store.put({ assignmentId, lmaNumber });

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  static async getLmaNumber(assignmentId: Uuid): Promise<string | undefined> {
    const db = await this.getDB();
    const transaction = db.transaction([this.STORES.LMA_NUMBERS], "readonly");
    const store = transaction.objectStore(this.STORES.LMA_NUMBERS);

    return new Promise((resolve, reject) => {
      const request = store.get(assignmentId);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const result = request.result;
        if (result && result.lmaNumber) {
          resolve(result.lmaNumber);
        } else {
          resolve(undefined);
        }
      };
    });
  }

  static async getAllLmaNumbers(): Promise<Record<string, string>> {
    const db = await this.getDB();
    const transaction = db.transaction([this.STORES.LMA_NUMBERS], "readonly");
    const store = transaction.objectStore(this.STORES.LMA_NUMBERS);

    return new Promise((resolve, reject) => {
      const request = store.getAll();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const results = request.result;
        const lmaMap: Record<string, string> = {};

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const result of results) {
          lmaMap[result.assignmentId] = result.lmaNumber;
        }

        resolve(lmaMap);
      };
    });
  }

  static async storeCustomerFile(
    assignmentId: Uuid,
    file: File
  ): Promise<void> {
    const db = await this.getDB();
    const transaction = db.transaction(
      [this.STORES.CUSTOMER_FILES],
      "readwrite"
    );
    const store = transaction.objectStore(this.STORES.CUSTOMER_FILES);

    return new Promise((resolve, reject) => {
      const { name, lastModified, type } = file;
      const data = file; // A File is a Blob.

      const request = store.add({
        assignmentId,
        /* cspell: disable-next-line */
        fileId: "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) =>
          (c == "x"
            ? (Math.random() * 16) | 0
            : (Math.random() * 4) | (0 + 8)
          ).toString(16)
        ), // Dummy UUIDv4 Generator.
        name,
        lastModified,
        type,
        data,
      });

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  static async getCustomerFile(fileId: Uuid): Promise<File | undefined> {
    const db = await this.getDB();
    const transaction = db.transaction(
      [this.STORES.CUSTOMER_FILES],
      "readonly"
    );
    const store = transaction.objectStore(this.STORES.CUSTOMER_FILES);
    const index = store.index("fileId");

    return new Promise((resolve, reject) => {
      const request = index.get(fileId);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const result = request.result;

        if (
          result &&
          result.name &&
          result.lastModified &&
          result.type &&
          result.data
        ) {
          resolve(
            new File([result.data], result.name, {
              lastModified: result.lastModified,
              type: result.type,
            })
          );
        } else {
          resolve(undefined);
        }
      };
    });
  }

  static async getCustomerFiles(
    assignmentId: Uuid
  ): Promise<{ id: Uuid; file: File }[]> {
    const db = await this.getDB();
    const transaction = db.transaction(
      [this.STORES.CUSTOMER_FILES],
      "readonly"
    );
    const store = transaction.objectStore(this.STORES.CUSTOMER_FILES);
    const index = store.index("assignmentId");

    return new Promise((resolve, reject) => {
      const request = index.getAll(assignmentId);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const results = request.result;
        const files = [];

        for (const result of results) {
          if (
            result.name &&
            result.lastModified &&
            result.type &&
            result.data
          ) {
            files.push({
              id: result.fileId,
              file: new File([result.data], result.name, {
                lastModified: result.lastModified,
                type: result.type,
              }),
            });
          }
        }

        resolve(files);
      };
    });
  }

  static async getAllCustomerFiles(): Promise<
    Record<string, { id: Uuid; file: File }[]>
  > {
    const db = await this.getDB();
    const transaction = db.transaction(
      [this.STORES.CUSTOMER_FILES],
      "readonly"
    );
    const store = transaction.objectStore(this.STORES.CUSTOMER_FILES);

    return new Promise((resolve, reject) => {
      const request = store.getAll();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const results = request.result;
        const filesMap: Record<string, { id: Uuid; file: File }[]> = {};

        for (const result of results) {
          if (
            result.assignmentId &&
            result.fileId &&
            result.name &&
            result.lastModified &&
            result.type &&
            result.data
          ) {
            if (!Array.isArray(filesMap[result.assignmentId])) {
              filesMap[result.assignmentId] = [];
            }

            (filesMap[result.assignmentId] as { id: Uuid; file: File }[]).push({
              id: result.fileId,
              file: new File([result.data], result.name, {
                lastModified: result.lastModified,
                type: result.type,
              }),
            });
          }
        }

        resolve(filesMap);
      };
    });
  }

  static async countCustomerFiles(): Promise<number> {
    const db = await this.getDB();
    const transaction = db.transaction(
      [this.STORES.CUSTOMER_FILES],
      "readonly"
    );
    const store = transaction.objectStore(this.STORES.CUSTOMER_FILES);

    return new Promise((resolve, reject) => {
      const countRequest = store.count();

      countRequest.onerror = () => reject(countRequest.error);
      countRequest.onsuccess = () => resolve(countRequest.result);
    });
  }

  private static pruneCustomerFilesLock = Promise.resolve();

  static async pruneCustomerFiles(): Promise<void> {
    await this.pruneCustomerFilesLock;

    const db = await this.getDB();
    const transaction = db.transaction(
      [this.STORES.CUSTOMER_FILES],
      "readwrite"
    );
    const store = transaction.objectStore(this.STORES.CUSTOMER_FILES);
    const count = await this.countCustomerFiles();

    if (count <= this.MAX_CUSTOMER_FILES) return;

    this.pruneCustomerFilesLock = new Promise((resolve, reject) => {
      const cursorRequest = store.openCursor();

      const recordsToDelete = count - this.MAX_CUSTOMER_FILES;
      let deletedCount = 0;

      cursorRequest.onerror = () => reject(cursorRequest.error);
      cursorRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;

        if (cursor && deletedCount < recordsToDelete) {
          const deleteRequest = cursor.delete();

          deleteRequest.onerror = () => reject(deleteRequest.error);
          deleteRequest.onsuccess = () => {
            deletedCount++;
            if (deletedCount < recordsToDelete) {
              cursor.continue(); // Move to next record
            } else {
              resolve(); // Done deleting
            }
          };
        } else {
          resolve(); // Cursor exhausted or done deleting
        }
      };
    });

    return this.pruneCustomerFilesLock;
  }

  static async storeAssignmentPatches(
    assignmentId: Uuid,
    operations: Operation[]
  ): Promise<void> {
    const db = await this.getDB();
    const transaction = db.transaction(
      [this.STORES.ASSIGNMENT_PATCHES],
      "readwrite"
    );
    const store = transaction.objectStore(this.STORES.ASSIGNMENT_PATCHES);

    return new Promise((resolve, reject) => {
      const getRequest = store.get(assignmentId);

      getRequest.onerror = () => reject(getRequest.error);
      getRequest.onsuccess = () => {
        // Append, since each patch is relative to the already patched data and
        // fake resources share this store with customer edits.
        const putRequest = store.put({
          assignmentId,
          operations: [...(getRequest.result?.operations ?? []), ...operations],
        });

        putRequest.onerror = () => reject(putRequest.error);
        putRequest.onsuccess = () => resolve();
      };
    });
  }

  static async getAssignmentPatches(assignmentId: Uuid): Promise<Operation[]> {
    const db = await this.getDB();
    const transaction = db.transaction(
      [this.STORES.ASSIGNMENT_PATCHES],
      "readonly"
    );
    const store = transaction.objectStore(this.STORES.ASSIGNMENT_PATCHES);

    return new Promise((resolve, reject) => {
      const request = store.get(assignmentId);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const result = request.result;
        if (result && result.operations) {
          resolve(result.operations);
        } else {
          resolve([]);
        }
      };
    });
  }

  static async getAllAssignmentPatches(): Promise<Record<string, Operation[]>> {
    const db = await this.getDB();
    const transaction = db.transaction(
      [this.STORES.ASSIGNMENT_PATCHES],
      "readonly"
    );
    const store = transaction.objectStore(this.STORES.ASSIGNMENT_PATCHES);

    return new Promise((resolve, reject) => {
      const request = store.getAll();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const results = request.result;
        const operationsMap: Record<string, Operation[]> = {};

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const result of results) {
          operationsMap[result.assignmentId] = result.operations;
        }

        resolve(operationsMap);
      };
    });
  }

  static async storeFeedbackProcessingComments(
    feedbackId: Uuid,
    extraComments: string
  ): Promise<void> {
    const db = await this.getDB();
    const transaction = db.transaction(
      [this.STORES.FEEDBACK_PROCESSING_COMMENTS],
      "readwrite"
    );
    const store = transaction.objectStore(
      this.STORES.FEEDBACK_PROCESSING_COMMENTS
    );

    return new Promise((resolve, reject) => {
      const request = store.put({ feedbackId, extraComments });

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  static async getFeedbackProcessingComments(
    feedbackId: Uuid
  ): Promise<string | undefined> {
    const db = await this.getDB();
    const transaction = db.transaction(
      [this.STORES.FEEDBACK_PROCESSING_COMMENTS],
      "readonly"
    );
    const store = transaction.objectStore(
      this.STORES.FEEDBACK_PROCESSING_COMMENTS
    );

    return new Promise((resolve, reject) => {
      const request = store.get(feedbackId);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const result = request.result;
        if (result && result.extraComments) {
          resolve(result.extraComments);
        } else {
          resolve(undefined);
        }
      };
    });
  }

  static async getAllFeedbackProcessingComments(): Promise<
    Record<string, string>
  > {
    const db = await this.getDB();
    const transaction = db.transaction(
      [this.STORES.FEEDBACK_PROCESSING_COMMENTS],
      "readonly"
    );
    const store = transaction.objectStore(
      this.STORES.FEEDBACK_PROCESSING_COMMENTS
    );

    return new Promise((resolve, reject) => {
      const request = store.getAll();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const results = request.result;
        const commentsMap: Record<string, string> = {};

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const result of results) {
          commentsMap[result.feedbackId] = result.extraComments;
        }

        resolve(commentsMap);
      };
    });
  }

  static async storeAgencySettings(
    agencyId: Uuid,
    settings: {
      assignmentRequestMinSkillLevel?: number;
      assignmentRequestInterval?: number;
      assignmentRequestResourceCount?: number;
      assignmentEmergencyDayReactionTime?: number;
      assignmentEmergencyNightReactionTime?: number;
    }
  ): Promise<void> {
    const db = await this.getDB();
    const transaction = db.transaction(
      [this.STORES.AGENCY_SETTINGS],
      "readwrite"
    );
    const store = transaction.objectStore(this.STORES.AGENCY_SETTINGS);

    return new Promise((resolve, reject) => {
      const request = store.put({ agencyId, ...settings });

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  static async getAgencySettings(agencyId: Uuid): Promise<{
    assignmentRequestMinSkillLevel?: number;
    assignmentRequestInterval?: number;
    assignmentRequestResourceCount?: number;
    assignmentEmergencyDayReactionTime?: number;
    assignmentEmergencyNightReactionTime?: number;
  }> {
    const db = await this.getDB();
    const transaction = db.transaction(
      [this.STORES.AGENCY_SETTINGS],
      "readonly"
    );
    const store = transaction.objectStore(this.STORES.AGENCY_SETTINGS);

    return new Promise((resolve, reject) => {
      const request = store.get(agencyId);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { agencyId, ...settings } = result;
          resolve(settings);
        } else {
          resolve({});
        }
      };
    });
  }

  static async storeFeedbackTypeWeight(
    feedbackTypeId: Uuid,
    weight: number
  ): Promise<void> {
    const db = await this.getDB();
    const transaction = db.transaction(
      [this.STORES.FEEDBACK_TYPE_WEIGHTS],
      "readwrite"
    );
    const store = transaction.objectStore(this.STORES.FEEDBACK_TYPE_WEIGHTS);

    return new Promise((resolve, reject) => {
      const request = store.put({ feedbackTypeId, weight });

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  static async getFeedbackTypeWeight(
    feedbackTypeId: Uuid
  ): Promise<number | undefined> {
    const db = await this.getDB();
    const transaction = db.transaction(
      [this.STORES.FEEDBACK_TYPE_WEIGHTS],
      "readonly"
    );
    const store = transaction.objectStore(this.STORES.FEEDBACK_TYPE_WEIGHTS);

    return new Promise((resolve, reject) => {
      const request = store.get(feedbackTypeId);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const result = request.result;
        if (result && typeof result.weight === "number") {
          resolve(result.weight);
        } else {
          resolve(undefined);
        }
      };
    });
  }

  static async getAllFeedbackTypeWeights(): Promise<Record<string, number>> {
    const db = await this.getDB();
    const transaction = db.transaction(
      [this.STORES.FEEDBACK_TYPE_WEIGHTS],
      "readonly"
    );
    const store = transaction.objectStore(this.STORES.FEEDBACK_TYPE_WEIGHTS);

    return new Promise((resolve, reject) => {
      const request = store.getAll();

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const results = request.result;
        const weightsMap: Record<string, number> = {};

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const result of results) {
          if (result.feedbackTypeId && typeof result.weight === "number") {
            weightsMap[result.feedbackTypeId] = result.weight;
          }
        }

        resolve(weightsMap);
      };
    });
  }
}

type DemoTransformRequest = (
  method: HttpMethod,
  request: ApiRequest
) => Promise<ApiRequest>;
type DemoTransformResponse = <T>(
  method: HttpMethod,
  response: ApiResponse<T>
) => Promise<ApiResponse<T>>;

type DemoHandler = {
  transformRequest?: DemoTransformRequest;
  transformResponse?: DemoTransformResponse;
};

function convertEmergencyToRegular(request: ApiRequest): ApiRequest {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = request.data as any;

  if (!data || !data.relativeStart || !data.relativeEnd) return request;

  // All enum steps are 5 min steps.
  const startMin = 5 * (data.relativeStart as number);

  const startTime = moment.tz(TimeZone).add(startMin, "minutes").add(1, "hour"); // API can't accept earlier yet.
  const endTime = startTime.clone().add(data.relativeEnd, "minutes");

  delete data.relativeStart;
  delete data.relativeEnd;

  data.start = startTime.toISOString(true);
  data.end = endTime.toISOString(true);

  return { ...request, data };
}

async function enhanceWithLmaNumbers<T>(
  response: ApiResponse<T>
): Promise<ApiResponse<T>> {
  if (!response.data) return response;

  try {
    if (typeof response.data === "object" && "id" in response.data) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const assignment = response.data as any;
      const lmaNumber = await DemoStorage.getLmaNumber(assignment.id);

      if (lmaNumber) {
        assignment.clientLmaNumber = lmaNumber;
      }

      return response;
    } else if (Array.isArray(response.data)) {
      const lmaNumbers = await DemoStorage.getAllLmaNumbers();

      const enhancedData = response.data.map((item) => {
        if (item.id && lmaNumbers[item.id]) {
          return { ...item, clientLmaNumber: lmaNumbers[item.id] };
        }
        return item;
      });

      return { ...response, data: enhancedData as unknown as T };
    }
  } catch (error: unknown) {
    log.warn("Failed to enhance with LMA numbers:", error);
  }

  return response;
}

async function enhanceWithCustomerFiles<T>(
  response: ApiResponse<T>
): Promise<ApiResponse<T>> {
  const assignmentId = response.request.ids
    ? response.request.ids[response.request.ids.length - 1]
    : undefined;
  if (!isUuid(assignmentId)) return response;

  try {
    const files = await DemoStorage.getCustomerFiles(assignmentId);
    if (files.length === 0) {
      response.code = HttpStatusCode.NO_CONTENT;
      response.data = undefined;
    } else {
      response.code = HttpStatusCode.OK;
      response.data = files.map(({ id, file }) => ({
        id,
        name: file.name,
        mimeType: file.type,
        size: file.size,
        isDeleted: false,
      })) as unknown as T;
    }
  } catch (error: unknown) {
    log.warn("Failed to enhance with Customer Files:", error);
  }

  return response;
}

async function enhanceWithAssignmentPatches<T>(
  response: ApiResponse<T>
): Promise<ApiResponse<T>> {
  try {
    if (typeof response.data === "object" && "id" in response.data) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const assignment = response.data as any;
      const patch = await DemoStorage.getAssignmentPatches(assignment.id);

      if (patch.length > 0) {
        applyPatch(assignment, patch, false, true);
      }

      return response;
    } else if (Array.isArray(response.data)) {
      const patches = await DemoStorage.getAllAssignmentPatches();

      const enhancedData = response.data.map((item) => {
        if (item.id) {
          const patch = patches[item.id];

          if (patch && patch.length > 0) {
            applyPatch(item, patch, false, true);
          }
        }

        return item;
      });

      return { ...response, data: enhancedData as unknown as T };
    }
  } catch (error: unknown) {
    log.warn("Failed to enhance with Assignment Patches:", error);
  }

  return response;
}

async function enhanceWithFeedbackProcessingComments<T>(
  response: ApiResponse<T>
): Promise<ApiResponse<T>> {
  if (!response.data) return response;

  try {
    if (typeof response.data === "object" && "id" in response.data) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const feedback = response.data as any;
      const extraComments = await DemoStorage.getFeedbackProcessingComments(
        feedback.id
      );

      if (extraComments && feedback.processingComments) {
        // Append extra comments to the existing processingComments
        feedback.processingComments =
          feedback.processingComments + extraComments;
      }

      return response;
    } else if (Array.isArray(response.data)) {
      const allExtraComments =
        await DemoStorage.getAllFeedbackProcessingComments();

      const enhancedData = response.data.map((item) => {
        if (item.id && allExtraComments[item.id]) {
          const enhanced = { ...item };
          if (enhanced.processingComments) {
            enhanced.processingComments =
              enhanced.processingComments + allExtraComments[item.id];
          }
          return enhanced;
        }
        return item;
      });

      return { ...response, data: enhancedData as unknown as T };
    }
  } catch (error: unknown) {
    log.warn("Failed to enhance with Feedback Processing Comments:", error);
  }

  return response;
}

async function enhanceWithAgencySettings<T>(
  response: ApiResponse<T>
): Promise<ApiResponse<T>> {
  if (!response.data) return response;

  try {
    if (typeof response.data === "object" && "agencies" in response.data) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const responseData = response.data as any;

      if (responseData.agencies && typeof responseData.agencies === "object") {
        // Iterate through each agency in the agencies object
        for (const agencyId of Object.keys(responseData.agencies)) {
          if (isUuid(agencyId)) {
            const extraSettings = await DemoStorage.getAgencySettings(agencyId);

            // Merge the stored settings into the agency's settings
            Object.assign(responseData.agencies[agencyId], extraSettings);
          }
        }
      }

      return response;
    }
  } catch (error: unknown) {
    log.warn("Failed to enhance with Agency Settings:", error);
  }

  return response;
}

async function enhanceWithFeedbackTypeWeights<T>(
  response: ApiResponse<T>
): Promise<ApiResponse<T>> {
  if (!response.data) return response;

  try {
    if (typeof response.data === "object" && "id" in response.data) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const feedbackType = response.data as any;
      const weight = await DemoStorage.getFeedbackTypeWeight(feedbackType.id);

      if (typeof weight === "number") {
        feedbackType.weight = weight;
      }

      return response;
    } else if (Array.isArray(response.data)) {
      const weights = await DemoStorage.getAllFeedbackTypeWeights();

      const enhancedData = response.data.map((item) => {
        if (item.id && weights[item.id]) {
          return { ...item, weight: weights[item.id] };
        }
        return item;
      });

      return { ...response, data: enhancedData as unknown as T };
    }
  } catch (error: unknown) {
    log.warn("Failed to enhance with Feedback Type Weights:", error);
  }

  return response;
}

const DEMO_HANDLERS: Record<string, DemoHandler> = {
  "agencies/assignments": {
    transformRequest: async (method, request) => {
      // Convert Emergency Bookings to Regular Assignments.
      if (
        method === HttpMethod.POST &&
        isObject(request.data) &&
        inObject("relativeStart", request.data, enumHasValue, RelativeStart)
      ) {
        request = convertEmergencyToRegular(request);
      }

      if (method === HttpMethod.PATCH && Array.isArray(request.data)) {
        const assignmentId = (
          request.ids && request.ids[request.ids.length - 1]
        )?.toString();

        if (isUuid(assignmentId)) {
          const lmaIndex = (request.data as Operation[]).findIndex(
            (operation) => operation.path === "/clientLmaNumber"
          );

          // Save LMA numbers in PATCH requests.
          if (lmaIndex !== -1) {
            const [operation] = request.data.splice(lmaIndex, 1) as (
              | AddOperation<string>
              | ReplaceOperation<string>
            )[];

            if (operation) {
              await DemoStorage.storeLmaNumber(assignmentId, operation.value);
            }
          }

          const resourceIdIndex = (request.data as Operation[]).findIndex(
            (operation) =>
              (operation.op === "add" || operation.op === "replace") &&
              operation.path === "/resourceId"
          );
          const statusIndex = (request.data as Operation[]).findIndex(
            (operation) =>
              operation.op === "replace" &&
              operation.path === "/status" &&
              operation.value === Status.ResourceConfirmed
          );

          // Save fake resource patches.
          if (
            resourceIdIndex !== -1 &&
            statusIndex !== -1 &&
            (request.data as Operation[]).length === 3
          ) {
            await DemoStorage.storeAssignmentPatches(
              assignmentId,
              request.data as Operation[]
            );
          }
        }
      }

      return request;
    },
    transformResponse: async (_method, response) => {
      // Save LMA numbers from 201 CREATED responses.
      if (
        response.code === HttpStatusCode.CREATED &&
        isObject(response.data) &&
        inObject("id", response.data, isUuid) &&
        isObject(response.request.data) &&
        inObject("clientLmaNumber", response.request.data, "string")
      ) {
        await DemoStorage.storeLmaNumber(
          response.data.id,
          response.request.data.clientLmaNumber
        );
      }

      // Apply LMA numbers and patches from storage.
      return enhanceWithLmaNumbers(response).then(enhanceWithAssignmentPatches);
    },
  },
  "agencies/assignments/customer-files": {
    transformResponse: async (method, response) => {
      // Save files being uploaded on 404 NOT FOUND responses.
      if (
        method === HttpMethod.POST &&
        response.code === HttpStatusCode.NOT_FOUND &&
        response.request.data instanceof FormData &&
        response.request.ids &&
        response.request.ids.length > 0 &&
        isUuid(response.request.ids[response.request.ids.length - 1])
      ) {
        const file = response.request.data.get("file");
        if (file instanceof File) {
          await DemoStorage.storeCustomerFile(
            response.request.ids[response.request.ids.length - 1] as Uuid,
            file
          );
          await DemoStorage.pruneCustomerFiles(); // Prune old files.
        }
      }

      return enhanceWithCustomerFiles(response);
    },
  },
  "agencies/assignments/feedbacks": {
    transformRequest: async (method, request) => {
      if (method === HttpMethod.PUT && isObject(request.data)) {
        const feedbackId = (
          request.ids && request.ids[request.ids.length - 1]
        )?.toString();

        if (
          isUuid(feedbackId) &&
          inObject("processingComments", request.data, "string") &&
          request.data.processingComments.length > 1000
        ) {
          // Find the last space before the 1000 character limit
          const fullText = request.data.processingComments;
          let splitIndex = 1000;

          // Look backwards from the 1000 char limit to find a space
          for (let i = 1000; i > 0; i--) {
            if (fullText[i] === " ") {
              splitIndex = i - 1;
              break;
            }
          }

          // Store the extra part (including the space) separately
          const baseComments = fullText.substring(0, splitIndex);
          const extraComments = fullText.substring(splitIndex);

          // Update the request data to only contain the first part
          request.data.processingComments = baseComments;

          // Store the extra part in IndexedDB
          await DemoStorage.storeFeedbackProcessingComments(
            feedbackId,
            extraComments
          );
        }
      }

      return request;
    },
    transformResponse: async (_method, response) => {
      // Apply extra processing comments from storage
      return enhanceWithFeedbackProcessingComments(response);
    },
  },
  "agencies/settings": {
    transformRequest: async (method, request) => {
      if (method === HttpMethod.PUT && isObject(request.data)) {
        const agencyId = (request.ids && request.ids[0])?.toString();

        if (isUuid(agencyId)) {
          const settingsToStore: {
            assignmentRequestMinSkillLevel?: number;
            assignmentRequestInterval?: number;
            assignmentRequestResourceCount?: number;
            assignmentEmergencyDayReactionTime?: number;
            assignmentEmergencyNightReactionTime?: number;
          } = {};

          // Extract and store the new settings fields, then remove them from the request
          if (
            ifInObject(
              "assignmentRequestMinSkillLevel",
              request.data,
              "number"
            ) &&
            request.data.assignmentRequestMinSkillLevel
          ) {
            settingsToStore.assignmentRequestMinSkillLevel =
              request.data.assignmentRequestMinSkillLevel;
            delete request.data.assignmentRequestMinSkillLevel;
          }

          if (
            ifInObject("assignmentRequestInterval", request.data, "number") &&
            request.data.assignmentRequestInterval
          ) {
            settingsToStore.assignmentRequestInterval =
              request.data.assignmentRequestInterval;
            delete request.data.assignmentRequestInterval;
          }

          if (
            ifInObject(
              "assignmentRequestResourceCount",
              request.data,
              "number"
            ) &&
            request.data.assignmentRequestResourceCount
          ) {
            settingsToStore.assignmentRequestResourceCount =
              request.data.assignmentRequestResourceCount;
            delete request.data.assignmentRequestResourceCount;
          }

          if (
            ifInObject(
              "assignmentEmergencyDayReactionTime",
              request.data,
              "number"
            ) &&
            request.data.assignmentEmergencyDayReactionTime
          ) {
            settingsToStore.assignmentEmergencyDayReactionTime =
              request.data.assignmentEmergencyDayReactionTime;
            delete request.data.assignmentEmergencyDayReactionTime;
          }

          if (
            ifInObject(
              "assignmentEmergencyNightReactionTime",
              request.data,
              "number"
            ) &&
            request.data.assignmentEmergencyNightReactionTime
          ) {
            settingsToStore.assignmentEmergencyNightReactionTime =
              request.data.assignmentEmergencyNightReactionTime;
            delete request.data.assignmentEmergencyNightReactionTime;
          }

          // Only store if we found any settings to store
          if (Object.keys(settingsToStore).length > 0) {
            await DemoStorage.storeAgencySettings(agencyId, settingsToStore);
          }
        }
      }

      return request;
    },
  },
  settings: {
    transformResponse: async (_method, response) => {
      // Apply extra settings from storage
      return enhanceWithAgencySettings(response);
    },
  },
  "agencies/feedback-types": {
    transformRequest: async (method, request) => {
      if (method === HttpMethod.PATCH && Array.isArray(request.data)) {
        const feedbackTypeId = (
          request.ids && request.ids[request.ids.length - 1]
        )?.toString();

        if (isUuid(feedbackTypeId)) {
          const weightIndex = (request.data as Operation[]).findIndex(
            (operation) => operation.path === "/weight"
          );

          // Save weight in PATCH requests.
          if (weightIndex !== -1) {
            const [operation] = request.data.splice(weightIndex, 1) as (
              | AddOperation<number>
              | ReplaceOperation<number>
            )[];

            if (operation && typeof operation.value === "number") {
              await DemoStorage.storeFeedbackTypeWeight(
                feedbackTypeId,
                operation.value
              );
            }
          }
        }
      }

      return request;
    },
    transformResponse: async (method, response) => {
      // Save weight from 201 CREATED responses.
      if (
        method === HttpMethod.POST &&
        response.code === HttpStatusCode.CREATED &&
        isObject(response.data) &&
        inObject("id", response.data, isUuid) &&
        isObject(response.request.data) &&
        inObject("weight", response.request.data, "number")
      ) {
        await DemoStorage.storeFeedbackTypeWeight(
          response.data.id,
          response.request.data.weight
        );
      }

      // Apply weight values from storage.
      return enhanceWithFeedbackTypeWeights(response);
    },
  },
};

// Add aliases to DEMO_HANDLERS.
DEMO_HANDLERS["agencies/customers/assignments"] = {
  transformRequest: async (method, request) => {
    const transformedRequest =
      (await DEMO_HANDLERS["agencies/assignments"]?.transformRequest?.(
        method,
        request
      )) ?? request;

    // Save customer edits, unlike agents who only save fake resource patches.
    if (method === HttpMethod.PATCH && Array.isArray(transformedRequest.data)) {
      const { data, ids } = transformedRequest;
      const assignmentId = (ids && ids[ids.length - 1])?.toString();

      if (isUuid(assignmentId) && data.length > 0) {
        try {
          await DemoStorage.storeAssignmentPatches(
            assignmentId,
            data as Operation[]
          );
        } catch (error: unknown) {
          log.warn("Failed to store customer assignment patches:", error);
        }
      }
    }

    return transformedRequest;
  },
  transformResponse: DEMO_HANDLERS["agencies/assignments"]?.transformResponse,
};

export function getDemoHandler(path: string): DemoHandler | undefined {
  // Paths are pre-normalized so we can just array access them.
  return DEMO_HANDLERS[path];
}
